import type { SelectOption } from '@/_components';
import { currencyInput } from '@/_schemas/generic';

import type {
  AgreementInstallmentFormSchema,
  AgreementPaymentFormSchema,
} from './schemas/agreement-installments-form-schema';
import type {
  AgreementPaymentCreateSchema,
  AgreementPaymentMutationSchema,
} from './schemas/agreement-installments-mutation-schema';
import type {
  AgreementInstallmentsQuerySchema,
  AgreementPaymentQuerySchema,
  PaymentStatus,
  PaymentType,
} from './schemas/agreement-installments-query-schema';

type InstallmentStatus = 'pending' | 'partially_paid' | 'paid' | 'overdue';

export const getInstallmentStatus = (
  installment: AgreementInstallmentFormSchema,
  hasPayments: boolean
): InstallmentStatus => {
  if (installment.remaining_balance === 0 && hasPayments) return 'paid';
  // if (installment.due_date && dateTime.isPast(installment.due_date)) return 'overdue';
  if (installment.remaining_balance && hasPayments) return 'partially_paid';
  return 'pending';
};

export const installmentStatusTextMap: Record<InstallmentStatus, string> = {
  pending: 'Not Paid',
  partially_paid: 'Partially Paid',
  paid: 'Paid',
  overdue: 'Overdue',
};

export const installmentStatusColorMap: Record<InstallmentStatus, string> = {
  pending: 'text-warning',
  partially_paid: 'text-warning',
  paid: 'text-success',
  overdue: 'text-error',
};

export const paymentStatusColorMap: Record<PaymentStatus, string> = {
  paid: 'text-success',
  pending: 'text-warning',
  overpaid: 'text-success',
  credit: 'text-success',
  refund: 'text-error',
};

const paymentTypeLabelMap: Record<PaymentType, string> = {
  payment: 'Payment',
  refund: 'Refund',
};

const paymentStatusLabelMap: Record<PaymentStatus, string> = {
  paid: 'Paid',
  pending: 'Pending',
  overpaid: 'Overpaid',
  credit: 'Credit',
  refund: 'Refund',
};

export const paymentTypeOptions: SelectOption<PaymentType>[] = [
  { name: 'Payment', value: 'payment' },
  { name: 'Refund', value: 'refund' },
];

export const paymentStatusOptions: SelectOption<PaymentStatus>[] = [
  { name: 'Paid', value: 'paid' },
  { name: 'Pending', value: 'pending' },
  { name: 'Overpaid', value: 'overpaid' },
  { name: 'Credit', value: 'credit' },
  { name: 'Refund', value: 'refund' },
];

const paymentTypeToSelectOption = (paymentType: PaymentType): SelectOption<PaymentType> => {
  return { name: paymentTypeLabelMap[paymentType], value: paymentType };
};

const paymentStatusToSelectOption = (paymentStatus: PaymentStatus): SelectOption<PaymentStatus> => {
  return { name: paymentStatusLabelMap[paymentStatus], value: paymentStatus };
};

export const paymentQueryToForm = (payment: AgreementPaymentQuerySchema): AgreementPaymentFormSchema => {
  return {
    ...payment,
    payment_date: payment.payment_date,
    payment_type: paymentTypeToSelectOption(payment.payment_type),
    payment_status: paymentStatusToSelectOption(payment.payment_status),
    amount: currencyInput.queryToFormConverter(payment?.amount),
  };
};

export const paymentFormToCreateMutation = (payment: AgreementPaymentFormSchema): AgreementPaymentCreateSchema => {
  const { id: _, ...rest } = payment;
  return {
    ...rest,
    payment_type: payment.payment_type.value,
    payment_status: payment.payment_status.value,
    amount: currencyInput.formToUpdateConverter(payment.amount) ?? 0,
  };
};

export const paymentFormToMutation = (payment: AgreementPaymentFormSchema): AgreementPaymentMutationSchema => {
  return {
    ...payment,
    payment_type: payment.payment_type.value as PaymentType,
    payment_status: payment.payment_status.value as PaymentStatus,
    amount: currencyInput.formToUpdateConverter(payment.amount) ?? 0,
  };
};

export const installmentQueryToForm = (
  installment: AgreementInstallmentsQuerySchema
): AgreementInstallmentFormSchema => {
  return {
    ...installment,
    wholesale_amount: currencyInput.queryToFormConverter(installment?.wholesale_amount),
    contracted_amount: currencyInput.queryToFormConverter(installment?.contracted_amount),
    due_date: installment.due_date,
    remaining_balance: installment.remaining_balance || 0,
    agreement_payments: installment.agreement_payments?.map(paymentQueryToForm) || [],
    invoice: installment.invoice ?? null,
  };
};
